// 2024 Topps Star Wars High-Tek
//
// SOURCES & CONFIDENCE
// - BASE SET (100 cards x 7 design patterns): two-source verified — the official
//   Topps Ripped tiering article (ripped.topps.com) AND the cardsmithsbreaks
//   full checklist agree on the 100-card roster and the per-pattern diffractor
//   ladders. The per-pattern parallel ladders genuinely DIFFER (see below), so
//   each pattern is modeled as its own base sub-set.
// - INSERTS & AUTOGRAPHS: the sub-set NAMES and CARD COUNTS (5 inserts; 55 autos
//   + 14 B&W auto variation) are cross-confirmed by TraderCracks and
//   DiamondCardsOnline (independent of cardsmithsbreaks). Individual card names /
//   signer pairings remain specimen-unverified — no graded/eBay-sold specimen was
//   located — so treat per-card data as medium confidence.
//
// RECONCILIATIONS (source disagreed with itself / had obvious typos):
// - Base #58: "Kanan Jarrus" (6 of 7 pattern tables) vs "Caleb Dume" (Throne Room
//   table). Same character; used the majority spelling.
// - Autos A-MEW / A-NLB: source transposed the two rows. Assigned by the code
//   initials — A-MEW = Mary Elizabeth Winstead (Hera Syndulla), A-NLB = Natasha
//   Liu Bordizzo (Sabine Wren).
// - Auto A-TW: source typo "Taika Watiti" -> "Taika Waititi".
// - Fantas-Tek "F-M" (The Mandalorian) and "F-ZB" (Zorii Bliss) appeared in the
//   source with a "S-" prefix; treated as Fantas-Tek (the table's 25th/24th rows)
//   and renumbered to the F- family. Verify the exact card numbers.
//
// HELD pending specimen verification (confirmed to EXIST, but not yet added):
// - Dual Autographs (~10 cards; ladder Green /10, Red /5, Black 1/1) and
//   Double-Sided Dual Autographs (3 cards; Red /5, Black 1/1). Existence is
//   confirmed by TraderCracks + DiamondCardsOnline, but the per-card signer
//   pairings are checklist-only (probable aggregator echo of one Topps sell
//   sheet) with no graded/eBay-sold specimen found, so they are held until a
//   specimen confirms them. Two listed pairings (Harrison Ford + Peter Mayhew,
//   on a "DA-HF" Dual and a "DS-HS" Double-Sided) are almost certainly source
//   errors — Mayhew died in May 2019 — and must not be added without a specimen.
// - Original 1/1 Sketch Cards (no card-level checklist; 1/1 original art).
//
// NOTE: The CSV originally supplied as "2024 High-Tek" was actually a different,
// planet-themed (Hyperspace-family) set — its cards/sub-sets did not match this
// product. This file is built from the verified High-Tek sources, not that CSV.

// --- Base diffractor ladders (vary by pattern tier) -------------------------
// Throne Room / Hyperspace / Imperial / Laser: full 5-step ladder.
const LADDER_FULL = [
  { id: 'gold-rainbow-foil', name: 'Gold Rainbow Foil', print_run: 50, color: '#f0c040' },
  { id: 'orange-magma', name: 'Orange Magma Diffractor', print_run: 25, color: '#ff9100' },
  { id: 'green-cube', name: 'Green Cube Diffractor', print_run: 10, color: '#66bb6a' },
  { id: 'red-orbit', name: 'Red Orbit Diffractor', print_run: 5, color: '#ff1744' },
  { id: 'black-galactic', name: 'Black Galactic Diffractor', print_run: 1, color: '#b0b0b0' },
];

// Rebel: starts at /10 (no Gold /50, no Orange /25).
const LADDER_REBEL = [
  { id: 'green-cube', name: 'Green Cube Diffractor', print_run: 10, color: '#66bb6a' },
  { id: 'red-orbit', name: 'Red Orbit Diffractor', print_run: 5, color: '#ff1744' },
  { id: 'black-galactic', name: 'Black Galactic Diffractor', print_run: 1, color: '#b0b0b0' },
];

// Death Star Interior / Exterior: start at /25 (no Gold /50).
const LADDER_DEATH_STAR = [
  { id: 'orange-magma', name: 'Orange Magma Diffractor', print_run: 25, color: '#ff9100' },
  { id: 'green-cube', name: 'Green Cube Diffractor', print_run: 10, color: '#66bb6a' },
  { id: 'red-orbit', name: 'Red Orbit Diffractor', print_run: 5, color: '#ff1744' },
  { id: 'black-galactic', name: 'Black Galactic Diffractor', print_run: 1, color: '#b0b0b0' },
];

// Inserts: Green /10, Red /5, Black 1/1.
const INSERT_LADDER = [
  { id: 'green-cube', name: 'Green Cube Diffractor', print_run: 10, color: '#66bb6a' },
  { id: 'red-orbit', name: 'Red Orbit Diffractor', print_run: 5, color: '#ff1744' },
  { id: 'black-galactic', name: 'Black Galactic Diffractor', print_run: 1, color: '#b0b0b0' },
];

// Autographs: Gold /50, Orange /25, Green /10, Red /5, Black 1/1. Gold /50 added
// per eBay sold specimen (A-RA Riz Ahmed / Bodhi Rook "/50 Gold Refractor");
// the cardsmithsbreaks checklist omits it, but a graded/sold specimen outranks it.
const AUTO_LADDER = [
  { id: 'gold-rainbow-foil', name: 'Gold Rainbow Foil', print_run: 50, color: '#f0c040' },
  { id: 'orange-magma', name: 'Orange Magma Diffractor', print_run: 25, color: '#ff9100' },
  { id: 'green-cube', name: 'Green Cube Diffractor', print_run: 10, color: '#66bb6a' },
  { id: 'red-orbit', name: 'Red Orbit Diffractor', print_run: 5, color: '#ff1744' },
  { id: 'black-galactic', name: 'Black Galactic Diffractor', print_run: 1, color: '#b0b0b0' },
];

// The 100-card base roster is identical across all 7 patterns; defined once and
// reused so each pattern sub-set stays in sync.
const BASE_NAMES = [
  'Aayla Secura', 'Admiral Ackbar', 'Admiral Motti', 'Admiral Piett', 'Ahsoka Tano',
  'Vice Admiral Amilyn Holdo', 'Anakin Skywalker', 'Bail Organa', 'Baylan Skoll', 'Baze Malbus',
  'BB-8', 'Bib Fortuna', 'Biggs Darklighter', 'Boba Fett', 'Bo-Katan Kryze',
  'Bossk', 'C-3PO', 'Cad Bane', 'Captain Rex', 'Carson Teva',
  'Cassian Andor', 'Chirrut Îmwe', 'Chopper', 'Cobb Vanth', 'Count Dooku',
  'Darth Vader', 'Darth Maul', 'Dedra Meero', 'Director Orson Krennic', 'DJ',
  'Emperor Palpatine', 'Enfys Nest', 'EV-9D9', 'Ezra Bridger', 'Fennec Shand',
  'Fifth Brother', "Figrin D'an", 'Finn', 'Galen Erso', 'Zeb Orrelios',
  'General Grievous', 'Grand Admiral Thrawn', 'Grand Moff Tarkin', 'Greedo', 'Greef Karga',
  'Grogu', 'Han Solo', 'Hera Syndulla', 'Huyang', 'IG-11',
  'IG-88', 'Jabba the Hutt', 'Jango Fett', 'Jar Jar Binks', 'Jek Porkins',
  'Jyn Erso', 'K-2SO', 'Kanan Jarrus', 'Ki-Adi-Mundi', 'Kit Fisto',
  'Kuiil', 'Kylo Ren', 'L3-37', 'Lando Calrissian', 'Leia Organa',
  'Lobot', 'Luke Skywalker', 'Luthen Rael', 'Mace Windu', 'Max Rebo',
  'Maz Kanata', 'Moff Gideon', 'Mon Mothma', 'Nien Nunb', 'Obi-Wan Kenobi',
  'Padmé Amidala', 'Paige Tico', 'Peli Motto', 'Plo Koon', 'Poe Dameron',
  'Ponda Baba', "Qi'ra", 'Qui-Gon Jinn', 'R2-D2', 'R5-D4',
  'Rey', 'Rose Tico', 'Sabine Wren', 'Salacious B. Crumb', 'Saw Gerrera',
  'Sebulba', 'Sy Snootles', 'The Client', 'The Grand Inquisitor', 'The Mandalorian',
  'Tusken Raider', 'Wedge Antilles', 'Wicket W. Warrick', 'Yoda', 'Zam Wesell',
];

const baseCards = BASE_NAMES.map((name, i) => ({ n: String(i + 1), name }));

export const SETS = [
  {
    id: 'base-throne-room', type: 'base', title: 'BASE — THRONE ROOM — 100 CARDS (3 per pack)',
    parallels: LADDER_FULL, cards: baseCards,
  },
  {
    id: 'base-hyperspace', type: 'base', title: 'BASE — HYPERSPACE — 100 CARDS (2 per pack)',
    parallels: LADDER_FULL, cards: baseCards,
  },
  {
    id: 'base-imperial', type: 'base', title: 'BASE — IMPERIAL WALLS — 100 CARDS (1 per pack)',
    parallels: LADDER_FULL, cards: baseCards,
  },
  {
    id: 'base-laser', type: 'base', title: 'BASE — DEATH STAR LASER — 100 CARDS (1:4 packs)',
    parallels: LADDER_FULL, cards: baseCards,
  },
  {
    id: 'base-rebel', type: 'base', title: 'BASE — REBEL SCREEN — 100 CARDS (1:12 packs)',
    parallels: LADDER_REBEL, cards: baseCards,
  },
  {
    id: 'base-death-star-interior', type: 'base', title: 'BASE — DEATH STAR INTERIOR — 100 CARDS (1 per box)',
    parallels: LADDER_DEATH_STAR, cards: baseCards,
  },
  {
    id: 'base-death-star-exterior', type: 'base', title: 'BASE — DEATH STAR EXTERIOR — 100 CARDS (1 per box)',
    parallels: LADDER_DEATH_STAR, cards: baseCards,
  },
  {
    id: 'black-and-white-variation', type: 'insert', title: 'BLACK & WHITE VARIATION — 25 CARDS', parallels: INSERT_LADDER,
    cards: [
      {n:'BW-AT', name:'Ahsoka Tano'},
      {n:'BW-BB', name:'BB-8'},
      {n:'BW-BF', name:'Boba Fett'},
      {n:'BW-C', name:'C-3PO'},
      {n:'BW-DM', name:'Darth Maul'},
      {n:'BW-DV', name:'Darth Vader'},
      {n:'BW-EP', name:'Emperor Palpatine'},
      {n:'BW-G', name:'Grogu'},
      {n:'BW-GG', name:'General Grievous'},
      {n:'BW-HS', name:'Han Solo'},
      {n:'BW-JH', name:'Jabba the Hutt'},
      {n:'BW-K', name:'K-2SO'},
      {n:'BW-KR', name:'Kylo Ren'},
      {n:'BW-LC', name:'Lando Calrissian'},
      {n:'BW-LO', name:'Leia Organa'},
      {n:'BW-LS', name:'Luke Skywalker'},
      {n:'BW-M', name:'The Mandalorian'},
      {n:'BW-MW', name:'Mace Windu'},
      {n:'BW-OWK', name:'Obi-Wan Kenobi'},
      {n:'BW-PA', name:'Padmé Amidala'},
      {n:'BW-PD', name:'Poe Dameron'},
      {n:'BW-QGJ', name:'Qui-Gon Jinn'},
      {n:'BW-R', name:'Rey'},
      {n:'BW-R2', name:'R2-D2'},
      {n:'BW-Y', name:'Yoda'},
    ]
  },
  {
    id: 'spectacular-species', type: 'insert', title: 'SPECTACULAR SPECIES — 20 CARDS', parallels: INSERT_LADDER,
    cards: [
      {n:'S-C', name:'Clawdite (Zam Wesell)'},
      {n:'S-E', name:'Ewok (Chief Chirpa)'},
      {n:'S-GE', name:'Geonosian (Poggle the Lesser)'},
      {n:'S-GN', name:'Gungan (Captain Tarpals)'},
      {n:'S-H', name:'Hutt (Jabba the Hutt)'},
      {n:'S-HM', name:'Human (Tobias Beckett)'},
      {n:'S-J', name:'Jawa (Offworld Jawa Elder)'},
      {n:'S-KD', name:'Kel Dor (Plo Koon)'},
      {n:'S-KM', name:'Kaminoan (Lama Su)'},
      {n:'S-L', name:'Lasat (Gron)'},
      {n:'S-MC', name:'Mon Calamari (Nahdar Vebb)'},
      {n:'S-N', name:'Nautolan (Kit Fisto)'},
      {n:'S-R', name:'Rodian (Glem)'},
      {n:'S-T', name:'Trandoshan (Bossk)'},
      {n:'S-TD', name:'Toydarian (Watto)'},
      {n:'S-TK', name:'Tusken (Tusken Raider)'},
      {n:'S-TL', name:"Twi'lek (Bib Fortuna)"},
      {n:'S-TR', name:'Togruta (Shaak Ti)'},
      {n:'S-W', name:'Wookiee (Krrsantan)'},
      {n:'S-Z', name:'Zabrak (Savage Opress)'},
    ]
  },
  {
    id: 'shadow-tek', type: 'insert', title: 'SHADOW-TEK — 20 CARDS', parallels: INSERT_LADDER,
    cards: [
      {n:'ST-AS', name:'Aurra Sing'},
      {n:'ST-AT', name:'Grand Admiral Thrawn'},
      {n:'ST-AV', name:'Asajj Ventress'},
      {n:'ST-BF', name:'Boba Fett'},
      {n:'ST-BO', name:'Barriss Offee'},
      {n:'ST-CB', name:'Cad Bane'},
      {n:'ST-DD', name:'Darth Tyranus'},
      {n:'ST-DM', name:'Darth Maul'},
      {n:'ST-DV', name:'Darth Vader'},
      {n:'ST-EP', name:'Emperor Palpatine'},
      {n:'ST-GG', name:'General Grievous'},
      {n:'ST-GH', name:'General Hux'},
      {n:'ST-GI', name:'The Grand Inquisitor'},
      {n:'ST-J', name:'Jabba the Hutt'},
      {n:'ST-KR', name:'Kylo Ren'},
      {n:'ST-MG', name:'Moff Gideon'},
      {n:'ST-MT', name:'Grand Moff Tarkin'},
      {n:'ST-OK', name:'Director Orson Krennic'},
      {n:'ST-S', name:'Supreme Leader Snoke'},
      {n:'ST-SO', name:'Savage Opress'},
    ]
  },
  {
    id: 'fantas-tek', type: 'insert', title: 'FANTAS-TEK — 25 CARDS', parallels: INSERT_LADDER,
    cards: [
      {n:'F-AS', name:'Anakin Skywalker'},
      {n:'F-AT', name:'Ahsoka Tano'},
      {n:'F-BF', name:'Boba Fett'},
      {n:'F-C', name:'Chewbacca'},
      {n:'F-CA', name:'Cassian Andor'},
      {n:'F-DM', name:'Darth Maul'},
      {n:'F-DV', name:'Darth Vader'},
      {n:'F-F', name:'Finn'},
      {n:'F-FS', name:'Fennec Shand'},
      {n:'F-G', name:'Grogu'},
      {n:'F-HS', name:'Han Solo'},
      {n:'F-K', name:'Krrsantan'},
      {n:'F-KS', name:'Kylo Ren'},
      {n:'F-LC', name:'Lando Calrissian'},
      {n:'F-LO', name:'Leia Organa'},
      {n:'F-LS', name:'Luke Skywalker'},
      {n:'F-MW', name:'Mace Windu'},
      {n:'F-OWK', name:'Obi-Wan Kenobi'},
      {n:'F-PA', name:'Padmé Amidala'},
      {n:'F-R', name:'Rey'},
      {n:'F-R2', name:'R2-D2'},
      {n:'F-S', name:'Stormtrooper'},
      {n:'F-ST', name:'Sith Trooper'},
      {n:'F-M', name:'The Mandalorian'},
      {n:'F-ZB', name:'Zorii Bliss'},
    ]
  },
  {
    id: 'wookiee-tek', type: 'insert', title: 'WOOKIEE-TEK — 10 CARDS', parallels: INSERT_LADDER,
    cards: [
      {n:'W-B', name:'Burryaga'},
      {n:'W-C', name:'Chewbacca'},
      {n:'W-G', name:'Gungi'},
      {n:'W-KR', name:'Krrsantan'},
      {n:'W-L', name:'Lachichuk'},
      {n:'W-M', name:'Captain Merumeru'},
      {n:'W-S', name:'Sagwa'},
      {n:'W-T', name:'Tarfful'},
      {n:'W-W', name:'Wullffwarro'},
      {n:'W-Y', name:'Yanna'},
    ]
  },
  {
    id: 'autograph-cards', type: 'auto', title: 'AUTOGRAPH CARDS — 55 CARDS', parallels: AUTO_LADDER,
    cards: [
      {n:'A-AA', name:'Adria Arjona', artist:'Bix Caleen'},
      {n:'A-AB', name:'Ahmed Best', artist:'Kelleran Beq'},
      {n:'A-AD', name:'Adam Driver', artist:'Kylo Ren'},
      {n:'A-BDT', name:'Benicio Del Toro', artist:'DJ'},
      {n:'A-BDW', name:'Billy Dee Williams', artist:'Lando Calrissian'},
      {n:'A-DB', name:'Dante Briggins', artist:'Stormtrooper'},
      {n:'A-DG', name:'Domhnall Gleeson', artist:'General Hux'},
      {n:'A-DL', name:'Diego Luna', artist:'Cassian Andor'},
      {n:'A-DM', name:'Denise Gough', artist:'Dedra Meero'},
      {n:'A-DR', name:'Daisy Ridley', artist:'Rey'},
      {n:'A-DY', name:'Donnie Yen', artist:'Chirrut Îmwe'},
      {n:'A-EC', name:'Emilia Clarke', artist:"Qi'ra"},
      {n:'A-EM', name:'Ewan McGregor', artist:'Obi-Wan Kenobi'},
      {n:'A-FJ', name:'Felicity Jones', artist:'Jyn Erso'},
      {n:'A-FPJ', name:'Freddie Prinze Jr.', artist:'Kanan Jarrus'},
      {n:'A-FW', name:'Forest Whitaker', artist:'Saw Gerrera'},
      {n:'A-GOR', name:"Genevieve O'Reilly", artist:'Mon Mothma'},
      {n:'A-HC', name:'Hayden Christensen', artist:'Anakin Skywalker'},
      {n:'A-HF', name:'Harrison Ford', artist:'Han Solo'},
      {n:'A-HQ', name:'Hugh Quarshie', artist:'Captain Panaka'},
      {n:'A-IM', name:'Ian McDiarmid', artist:'Darth Sidious'},
      {n:'A-IS', name:'Ivanna Sakhno', artist:'Shin Hati'},
      {n:'A-JB', name:'Jennifer Beals', artist:'Garsa Fwip'},
      {n:'A-JC', name:'Jim Cummings', artist:'Hondo Ohnaka'},
      {n:'A-JG', name:'Julian Glover', artist:'General Veers'},
      {n:'A-KCS', name:'Karol Cristina da Silva', artist:'Rabé'},
      {n:'A-KK', name:'Keira Knightley', artist:'Sabé'},
      {n:'A-KS', name:'Katee Sackhoff', artist:'Bo-Katan Kryze'},
      {n:'A-LM', name:'Lars Mikkelsen', artist:'Grand Admiral Thrawn'},
      {n:'A-LN', name:"Lupita Nyong'o", artist:'Maz Kanata'},
      {n:'A-LS', name:'Leilani Shiu', artist:'Teeka the Jawa'},
      {n:'A-MC', name:'Michaela Cottrell', artist:'Even Piell'},
      {n:'A-ME', name:'Maya Erskine', artist:'Sully Stark'},
      {n:'A-MEW', name:'Mary Elizabeth Winstead', artist:'Hera Syndulla'},
      {n:'A-MH', name:'Mark Hamill', artist:'Luke Skywalker'},
      {n:'A-MNW', name:'Ming-Na Wen', artist:'Fennec Shand'},
      {n:'A-NK', name:'Nalini Krishan', artist:'Barriss Offee'},
      {n:'A-NLB', name:'Natasha Liu Bordizzo', artist:'Sabine Wren'},
      {n:'A-NN', name:'Nick Nolte', artist:'Kuiil'},
      {n:'A-NP', name:'Natalie Portman', artist:'Padmé Amidala'},
      {n:'A-PL', name:'Phil LaMarr', artist:'Kit Fisto'},
      {n:'A-PM', name:'Amy Sedaris', artist:'Peli Motto'},
      {n:'A-RA', name:'Riz Ahmed', artist:'Bodhi Rook'},
      {n:'A-RD', name:'Rosario Dawson', artist:'Ahsoka Tano'},
      {n:'A-RF', name:'Rupert Friend', artist:'The Grand Inquisitor'},
      {n:'A-RP', name:'Ray Park', artist:'Darth Maul'},
      {n:'A-SJ', name:'Samuel L. Jackson', artist:'Mace Windu'},
      {n:'A-SLS', name:'Andy Serkis', artist:'Supreme Leader Snoke'},
      {n:'A-SS', name:'Stellan Skarsgård', artist:'Luthen Rael'},
      {n:'A-TM', name:'Temuera Morrison', artist:'Boba Fett'},
      {n:'A-TO', name:'Timothy Olyphant', artist:'Cobb Vanth'},
      {n:'A-TR', name:'Tim Rose', artist:'Admiral Ackbar'},
      {n:'A-TW', name:'Taika Waititi', artist:'IG-11'},
      {n:'A-WD', name:'Warwick Davis', artist:'Wicket W. Warrick'},
      {n:'A-WH', name:'Werner Herzog', artist:'The Client'},
    ]
  },
  {
    id: 'black-and-white-autograph-variation', type: 'auto', title: 'BLACK & WHITE AUTOGRAPH VARIATION — 14 CARDS', parallels: [],
    cards: [
      {n:'ABW-AT', name:'Rosario Dawson', artist:'Ahsoka Tano'},
      {n:'ABW-BF', name:'Temuera Morrison', artist:'Boba Fett'},
      {n:'ABW-DM', name:'Ray Park', artist:'Darth Maul'},
      {n:'ABW-DV', name:'Hayden Christensen', artist:'Darth Vader'},
      {n:'ABW-EP', name:'Ian McDiarmid', artist:'Emperor Palpatine'},
      {n:'ABW-HS', name:'Harrison Ford', artist:'Han Solo'},
      {n:'ABW-KR', name:'Adam Driver', artist:'Kylo Ren'},
      {n:'ABW-LC', name:'Billy Dee Williams', artist:'Lando Calrissian'},
      {n:'ABW-LS', name:'Mark Hamill', artist:'Luke Skywalker'},
      {n:'ABW-MW', name:'Samuel L. Jackson', artist:'Mace Windu'},
      {n:'ABW-OWK', name:'Ewan McGregor', artist:'Obi-Wan Kenobi'},
      {n:'ABW-PA', name:'Natalie Portman', artist:'Padmé Amidala'},
      {n:'ABW-PD', name:'Oscar Isaac', artist:'Poe Dameron'},
      {n:'ABW-R', name:'Daisy Ridley', artist:'Rey'},
    ]
  },
];

export const ALL_CARD_KEYS = SETS.flatMap(set =>
  set.cards.map(card => `${set.id}__${card.n}`)
);

export const PARALLELS = LADDER_FULL;
export const AUTO_PARALLELS = AUTO_LADDER;
export const INSERT_PARALLELS = INSERT_LADDER;
